<script setup lang="ts">
import { computed } from 'vue';
import {hapticFeedback} from "@tma.js/sdk-vue";
import CoreIcon, { CoreIconProps } from "@core/core-icon.vue";
import CoreLoader from "@/components/core/core-loader.vue";
import isInTelegram from "@/utils/isInTelegram";

export interface ButtonPrimaryProps {
	title?: string,
	to?: string,
	url?: string,
	icon?: CoreIconProps,
	target?: string,
	pink?: boolean,
	red?: boolean,
	gray?: boolean,
	font?: string,
	disabled?: boolean,
	loading?: boolean,
}

const props = withDefaults(defineProps<ButtonPrimaryProps>(), {
	font: 'font-16-pro-regular',
	disabled: undefined
});

const modes = computed(() => ({
	'button-primary_pink': props.pink,
	'button-primary_red': props.red,
	'button-primary_gray': props.gray,
}));
const hapticHandler = async () => {
	if (isInTelegram() && hapticFeedback.impactOccurred.isAvailable()) {
		hapticFeedback.impactOccurred('medium');
	}
}
</script>

<template>
	<component
		:is="to ? 'router-link' : url ? 'a' : 'button'"
		:to="to || null"
		:target="target || null"
		:href="url || null"
		:class="['button-primary', font, modes]"
		:disabled="disabled"
		@click.native="hapticHandler"
	>
		<core-loader
			v-if="loading"
			class="button-primary__loading"
		/>
		<template v-else>
			<span class="button-primary__text">
				<template v-if="title">{{ title }}</template>
				<slot v-else />
			</span>
			<core-icon v-if="icon" v-bind="icon" class="button-primary__icon" />
		</template>
	</component>
</template>

<style lang="scss">

.button-primary {
	height: 5.2rem;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 7.2rem;
	padding: 0 3rem;
	gap: 1.2rem;
	cursor: pointer;
	position: relative;
	@include button-primary-surface;
	// Средняя насыщенность: класс шрифта из пропа задаёт только кегль
	font-weight: 500;

	@include min-desktop {
		@include font-18-pro-medium;
	}

	// Второстепенные варианты: без градиента и свечения, подъём при
	// наведении общий с главной кнопкой
	&_pink,
	&_red,
	&_gray {
		box-shadow: none;

		@media (hover: hover) and (pointer: fine) {
			&:hover:not([disabled]) {
				filter: none;
				box-shadow: none;
			}
		}
	}

	&_pink {
		background: var(--color-pink);
		border: 1px solid var(--color-panel);
		color: var(--color-text);

		@media (hover: hover) and (pointer: fine) {
			&:hover:not([disabled]) {
				background: rgba(var(--color-active-rgb), 0.2);
			}
		}
		@include theme-dark {
			background: rgba(var(--color-active-rgb), 0.12);
			border-color: rgba(var(--color-active-rgb), 0.04);
			backdrop-filter: blur(3px);
		}
	}

	&_red {
		background: var(--color-red);
		color: var(--color-white);
	}

	&_gray {
		background: var(--color-gray);
		color: var(--color-text);
	}

	&__icon {
		width: var(--button-icon-size, 1.4rem);
		fill: currentColor;
		position: relative;
		z-index: 1;
	}
	&__text {
		position: relative;
		z-index: 1;
	}
	&__loading.core-loader {
		--loader-size: 2.4rem;
		--loader-color: var(--color-white);
		margin-bottom: 0;
		z-index: 1;
		position: relative;
	}
}
</style>
